// Relative with an explicit `.ts` extension, not `#/`-aliased: this file
// must stay importable from plain Node by the `server/head` service (phase
// 4a-1, Task 0), which has no bundler to resolve the `#/` alias and whose
// ESM resolver needs the extension spelled out (extensionless relative
// imports only work under Metro/Webpack/TS bundler-mode). Both targets are
// themselves dependency-free for the same reason - see their doc comments.
import {SUNNAHSKY_HANDLE_DOMAIN} from '../sunnahsky-origins.ts'
import {
  isCatcherHandle,
  isInvalidHandle,
  isSunnahskyHandle,
} from './sunnahsky-handle-checks.ts'

const CATCHER_SUFFIX = `.guest.${SUNNAHSKY_HANDLE_DOMAIN}`
const STRIKER_SUFFIX = `.${SUNNAHSKY_HANDLE_DOMAIN}`

/**
 * The identifier (a DID, or a full Sunnahsky handle) a `/:name` route
 * segment refers to - the inverse of {@link identifierToNameSegment}. Part
 * of "PDS hostname move and public URL scheme".
 *
 * - `did:*` passes through unchanged.
 * - No dot at all -> a Striker's short name (`johnsmith` ->
 *   `johnsmith.sunnahsky.com`).
 * - Ends in exactly `.guest` with no other dot -> a Catcher's short name
 *   (`jakecooper.guest` -> `jakecooper.guest.sunnahsky.com`).
 * - Anything else -> `undefined`, which the screen renders as Not Found.
 *
 * The brief this plan is based on called for passing any other dotted
 * segment through as an outside (non-Sunnahsky) handle - dropped in
 * security review (finding 1): it would render
 * `sunnahsky.com/johnsmith.bsky.social` as a canonical-looking page for an
 * unrelated Bluesky account under the platform's own root, bypassing the
 * leaving-the-app warning, and it contradicts the standing no-external-
 * domains rule (`account-manager.ts:218-226`). Outside profiles stay
 * reachable only through the existing `/profile/{handle}` alias.
 */
export function nameSegmentToIdentifier(segment: string): string | undefined {
  if (segment.startsWith('did:')) return segment
  if (!segment.includes('.')) return `${segment}${STRIKER_SUFFIX}`
  if (segment.endsWith('.guest')) {
    const name = segment.slice(0, -'.guest'.length)
    if (name.length > 0 && !name.includes('.')) {
      return `${name}${CATCHER_SUFFIX}`
    }
  }
  return undefined
}

/**
 * The `/:name` route segment for a given handle - the inverse of
 * {@link nameSegmentToIdentifier}. `undefined` for `handle.invalid` (no
 * segment can represent an unresolved handle) and for a handle outside
 * Sunnahsky entirely (no canonical root-level path exists for it).
 */
export function identifierToNameSegment(handle: string): string | undefined {
  if (isInvalidHandle(handle)) return undefined
  if (isCatcherHandle(handle)) {
    return handle.slice(0, -CATCHER_SUFFIX.length) + '.guest'
  }
  if (isSunnahskyHandle(handle)) {
    return handle.slice(0, -STRIKER_SUFFIX.length)
  }
  return undefined
}

const nameSegmentOrThrow = (handle: string): string => {
  const segment = identifierToNameSegment(handle)
  if (segment === undefined) {
    throw new Error(`No canonical /:name segment for handle: ${handle}`)
  }
  return segment
}

export function canonicalProfilePath(handle: string): string {
  return `/${nameSegmentOrThrow(handle)}`.toLowerCase()
}

export function canonicalPostPath(handle: string, rkey: string): string {
  return `/${nameSegmentOrThrow(handle)}/post/${rkey}`.toLowerCase()
}

export function canonicalArticlePath(handle: string, slug: string): string {
  return `/${nameSegmentOrThrow(handle)}/article/${slug}`.toLowerCase()
}
